import { useQueryClient } from "@tanstack/react-query";
import { Inbox, Plus, Star } from "lucide-react";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { Mailbox } from "../../api/types";
import {
  Card,
  CardHeader,
  DataTable,
  EditorPage,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  StatusPill,
} from "../../ui/components";
import { SkeletonTable } from "../../ui/skeletons";
import { useMailboxes } from "./queries";

const purposeLabels: Record<string, string> = {
  general: "General",
  bookings: "Bookings",
  events: "Events",
  marketing: "Marketing",
  support: "Support",
};

export function MailboxesPage() {
  const queryClient = useQueryClient();
  const mailboxes = useMailboxes();
  const [creating, setCreating] = useState(false);
  const [localPart, setLocalPart] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [purpose, setPurpose] = useState("general");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setWorking(true);
    setError(undefined);
    try {
      // The domain comes from the workspace's own mail domain, not from the user.
      await api<Mailbox>("/api/v1/messaging/mailboxes", {
        method: "POST",
        body: jsonBody({ localPart, displayName, purpose }),
      });
      await queryClient.invalidateQueries({ queryKey: ["messaging", "mailboxes"] });
      setCreating(false);
      setLocalPart("");
      setDisplayName("");
      setPurpose("general");
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  return (
    <EditorPage
      title="Mailboxes"
      description="Each mailbox is an email address your workspace can send and receive from. Your plan determines how many you can create."
      backTo="/emails"
      backLabel="Back to emails"
    >
      {error ? <ErrorPanel error={error} /> : null}
      <Card>
        <CardHeader
          title="Active mailboxes"
          description="Inbound email to any of these addresses appears in your conversation list. The default mailbox is used for outbound messages unless another is chosen."
          actions={
            <button
              className="button secondary small"
              type="button"
              onClick={() => setCreating(!creating)}
            >
              <Plus size={16} aria-hidden="true" /> Add mailbox
            </button>
          }
        />
        {mailboxes.error ? (
          <ErrorPanel error={mailboxes.error} />
        ) : mailboxes.isLoading ? (
          <SkeletonTable columns={5} rows={3} bare />
        ) : mailboxes.data?.items.length ? (
          <DataTable
            caption="Mailboxes"
            items={mailboxes.data.items}
            getKey={(mb) => mb.id}
            columns={[
              {
                label: "Address",
                sortValue: (mb) => mb.address,
                render: (mb) => (
                  <span className="inline">
                    <Inbox size={16} aria-hidden="true" />
                    <strong>{mb.address}</strong>
                    {mb.isDefault ? (
                      // "Default" alone is ambiguous once it is read out away from its column
                      // heading, so the noun is supplied to a screen reader and left off the badge.
                      // An aria-label was the previous attempt; a <span> has no role to carry it.
                      <span className="badge">
                        <Star size={12} aria-hidden="true" /> Default
                        <span className="sr-only"> mailbox</span>
                      </span>
                    ) : null}
                  </span>
                ),
              },
              {
                label: "Display name",
                sortValue: (mb) => mb.displayName,
                render: (mb) => mb.displayName || <span className="muted">Not set</span>,
              },
              {
                label: "Purpose",
                sortValue: (mb) => mb.purpose,
                render: (mb) => purposeLabels[mb.purpose] ?? mb.purpose,
              },
              {
                label: "Status",
                sortValue: (mb) => mb.status,
                render: (mb) => <StatusPill value={mb.status} />,
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No mailboxes"
            description="Create your first mailbox to start sending and receiving email. The first one becomes your default."
            action={
              <button className="button primary" type="button" onClick={() => setCreating(true)}>
                <Plus size={16} aria-hidden="true" /> Create mailbox
              </button>
            }
          />
        )}
      </Card>
      {creating ? (
        <Card>
          <CardHeader
            title="New mailbox"
            description="The address is formed from the local part you choose and your workspace's email domain. Reserved words like admin, postmaster, and abuse are not allowed."
          />
          <form className="form-grid" onSubmit={create}>
            <Field
              label="Local part"
              hint='The part before the @. For example, entering "bookings" creates bookings@your-domain.'
            >
              <input
                name="localPart"
                required
                pattern="[a-z0-9][a-z0-9._-]*[a-z0-9]"
                maxLength={64}
                value={localPart}
                onChange={(event) => setLocalPart(event.target.value.toLowerCase())}
                placeholder="e.g. bookings, events, support"
              />
            </Field>
            <Field
              label="Display name"
              hint="Shown as the sender name in outbound email. Leave empty to use your workspace name."
            >
              <input
                name="displayName"
                maxLength={120}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="e.g. Bookings Team"
              />
            </Field>
            <Field
              label="Purpose"
              hint="Helps categorize conversations. General works for most uses."
            >
              <select
                name="purpose"
                value={purpose}
                onChange={(event) => setPurpose(event.target.value)}
              >
                <option value="general">General</option>
                <option value="bookings">Bookings</option>
                <option value="events">Events</option>
                <option value="marketing">Marketing</option>
                <option value="support">Support</option>
              </select>
            </Field>
            <div className="full">
              <FormActions
                submitting={working}
                submitLabel="Create mailbox"
                onCancel={() => setCreating(false)}
              />
            </div>
          </form>
        </Card>
      ) : null}
    </EditorPage>
  );
}
